/**
 * Blog article edit history.
 *
 * Frontmatter keeps `date` as the original post. `edits` is newest-last.
 * The date we show anywhere public is `presentedDate` (last edit, else original).
 */

export type ArticleEdit = {
  date: string;
  note: string;
};

/** Coerce gray-matter YAML dates (string or Date) to YYYY-MM-DD. */
export function toArticleDateString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : trimmed || undefined;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return undefined;
}

export function parseArticleEdits(raw: unknown): ArticleEdit[] {
  if (!Array.isArray(raw)) return [];
  const edits: ArticleEdit[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const date = toArticleDateString((item as { date?: unknown }).date);
    const note = (item as { note?: unknown }).note;
    if (!date || typeof note !== 'string') continue;
    const trimmedNote = note.trim();
    if (!trimmedNote) continue;
    edits.push({ date, note: trimmedNote });
  }
  return edits;
}

export function presentedDate(date?: string, edits?: ArticleEdit[]): string | undefined {
  const last = edits?.[edits.length - 1]?.date;
  return last || date;
}

export function hasEdits(edits?: ArticleEdit[]): boolean {
  return (edits?.length ?? 0) > 0;
}

/** Format YYYY-MM-DD (or ISO) in local time so UTC midnight is not the previous day. */
export function formatArticleDate(
  date: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' },
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!match) {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString('en-US', options);
  }
  const local = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return local.toLocaleDateString('en-US', options);
}
